import React from 'react';
import { Flex, PieChart, Table } from '@components';
import { Portfolio as PortfolioContentType } from '@features/api/response/tabContent/Portfolio';
import { NivoGridCard } from '@components/Charts/common/NivoGridContainer';
import { TableRow, useTheme } from '@mui/material';
import { ChartTitle } from '@components/Charts/common/ChartTitle/ChartTitle';
import { FormattedMessage, useIntl } from 'react-intl';
import Loader from '@components/AppLoader/Loader';
import {
  Container,
  PortfolioContainer,
  StyledTableCell,
} from './Portfolio.styles';
import { useTabData } from '@hooks/useTabData';

import { TabType } from '@features/api/response/tabContent/TabType';

const Portfolio: React.FC = () => {
  const intl = useIntl();
  const theme = useTheme();

  const {
    data: content,
    isFetching,
    isLoading,
  } = useTabData<PortfolioContentType>(TabType.SEED_PORTFOLIO);

  if (!content) return <></>;

  return (
    <Container>
      <Loader isLoading={isFetching || isLoading} />
      <PortfolioContainer data-testid='investment-type-pie-chart'>
        <PieChart
          data={
            content.investmentTypeChart.chartData?.map((x) => ({
              id: x.name,
              value: x.percent,
            })) ?? []
          }
          title={intl.formatMessage({
            id: 'product.portfolio.charts.investmentType',
          })}
        />
      </PortfolioContainer>
      <PortfolioContainer data-testid='geographical-allocation-pie-chart'>
        <PieChart
          data={
            content.geographicalAllocationChart.chartData?.map((x) => ({
              id: x.name,
              value: x.percent,
            })) ?? []
          }
          title={intl.formatMessage({
            id: 'product.portfolio.charts.geography',
          })}
        />
      </PortfolioContainer>
      <PortfolioContainer data-testid='investment-strategy-pie-chart'>
        <PieChart
          data={
            content.investmentStrategyChart.chartData?.map((x) => ({
              id: x.name,
              value: x.percent,
            })) ?? []
          }
          title={intl.formatMessage({
            id: 'product.portfolio.charts.investmentStrategy',
          })}
        />
      </PortfolioContainer>
      <PortfolioContainer data-testid='target-investments-pie-chart'>
        <PieChart
          data={
            content.targetInvestmentsChart.chartData?.map((x) => ({
              id: x.name,
              value: x.percent,
            })) ?? []
          }
          title={intl.formatMessage({
            id: 'product.portfolio.charts.investmentSectors',
          })}
        />
      </PortfolioContainer>
      <Flex
        sx={{
          width: '100%',
          paddingBottom: {
            xs: '100px',
            md: 0,
          },
        }}
      >
        <NivoGridCard
          data-testid='portfolio-investments-table'
          sx={{ maxWidth: '100%' }}
        >
          <Flex
            sx={{
              margin: theme.spacing(6, 4),
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <ChartTitle
              title={intl.formatMessage({
                id: 'product.portfolio.portfolioInvestments',
              })}
            />
          </Flex>

          <Flex overflow='auto' maxWidth='100%'>
            <Table
              header={
                <TableRow>
                  <StyledTableCell align='left'>
                    <FormattedMessage id='product.portfolio.portfolioInvestmentsColumns.name' />
                  </StyledTableCell>
                  <StyledTableCell align='left'>
                    <FormattedMessage id='product.portfolio.portfolioInvestmentsColumns.investmentType' />
                  </StyledTableCell>
                  <StyledTableCell align='left'>
                    <FormattedMessage id='product.portfolio.portfolioInvestmentsColumns.strategy' />
                  </StyledTableCell>
                  <StyledTableCell align='left'>
                    <FormattedMessage id='product.portfolio.portfolioInvestmentsColumns.geography' />
                  </StyledTableCell>
                </TableRow>
              }
              body={content.portfolioInvestments?.map((x) => (
                <TableRow>
                  <StyledTableCell>{x.name}</StyledTableCell>
                  <StyledTableCell>
                    {x.investmentTypes
                      .map((c) =>
                        intl.formatMessage({
                          id: `product.portfolio.portfolioInvestmentsTypes.${c}`,
                        })
                      )
                      .join(', ')}
                  </StyledTableCell>
                  <StyledTableCell>{x.strategies}</StyledTableCell>
                  <StyledTableCell>{x.geography}</StyledTableCell>
                </TableRow>
              ))}
            />
          </Flex>
        </NivoGridCard>
      </Flex>
    </Container>
  );
};

export default Portfolio;
